import { Globe, Puzzle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { ConsentCheckbox } from "@/components/candidate/ConsentCheckbox";
import { Disclosure } from "@/components/candidate/Disclosure";

/** EF-4 — "Before you start: browser setup". The private-window shortcut up
 *  front, the per-browser instructions behind a disclosure (UX-17: the step
 *  used to be a wall of six bullets), a recommended browser, and ONE required
 *  acknowledgement. Honest by design: a web page cannot see or disable
 *  extensions, so nothing here checks anything — the candidate's tick is
 *  recorded on the attempt (`extensions_confirmed_at`) and the trace probe
 *  runs when they continue. That disclaimer and the tick are never collapsed.
 *  Never gated by plan. Shown on every test except one whose recruiter
 *  switched every anti-cheat check off (FEAT-25 — the Gate skips the step). */
export function BrowserSetupStep({
  confirmed,
  onConfirmedChange,
  onContinue,
}: {
  confirmed: boolean;
  onConfirmedChange: (v: boolean) => void;
  onContinue: () => void;
}) {
  return (
    <div className="flex min-h-full items-center justify-center bg-background p-6">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-2">
          <p className="text-xs uppercase tracking-wide text-muted-foreground">
            Before you start
          </p>
          <CardTitle as="h1" className="text-2xl">
            Turn off browser extensions
          </CardTitle>
          <CardDescription>
            Spell-checkers, translators, password managers and AI assistants
            can change the test page or be read as outside help. Please switch
            them off for the duration of the test.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <p className="text-sm text-foreground/90">
            The quickest way: open this link in a{" "}
            <span className="font-medium">private window</span>, where
            extensions are off by default.
          </p>
          <Disclosure label="How to do this in your browser" panelClassName="space-y-3">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Open a private window
            </p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <span className="font-medium text-foreground/90">Chrome / Edge:</span>{" "}
                Ctrl+Shift+N (⌘+Shift+N on Mac) — or a Guest profile.
              </li>
              <li>
                <span className="font-medium text-foreground/90">Firefox:</span>{" "}
                Ctrl+Shift+P (⌘+Shift+P on Mac).
              </li>
              <li>
                <span className="font-medium text-foreground/90">Safari:</span>{" "}
                File &rarr; New Private Window, then check Settings &rarr; Extensions.
              </li>
            </ul>
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Or turn them off in this window
            </p>
            <ul className="list-disc space-y-1 pl-5 text-muted-foreground">
              <li>
                <span className="font-medium text-foreground/90">Chrome:</span>{" "}
                <span className="font-mono text-xs">chrome://extensions</span> &mdash;
                switch each one off.
              </li>
              <li>
                <span className="font-medium text-foreground/90">Edge:</span>{" "}
                <span className="font-mono text-xs">edge://extensions</span>.
              </li>
              <li>
                <span className="font-medium text-foreground/90">Firefox:</span>{" "}
                <span className="font-mono text-xs">about:addons</span> &rarr; Extensions.
              </li>
            </ul>
            <p className="text-xs text-muted-foreground">
              Then reload this page before continuing.
            </p>
          </Disclosure>
          <p className="flex items-start gap-2 text-sm text-muted-foreground">
            <Globe className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
            <span>
              We recommend{" "}
              <span className="font-medium text-foreground/90">Google Chrome</span>{" "}
              on a laptop or desktop computer. Other up-to-date browsers work
              too.
            </span>
          </p>
          <div className="flex items-start gap-2 rounded-md border border-info/40 bg-info/5 px-3 py-2 text-xs text-muted-foreground">
            <Puzzle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            <span>
              We can&rsquo;t see or switch off your extensions ourselves. If an
              extension leaves visible signs on the page, that is noted for the
              recruiter as context &mdash; it is never treated as cheating on
              its own.
            </span>
          </div>
          <div className="border-t border-border pt-4">
            <ConsentCheckbox checked={confirmed} onChange={onConfirmedChange}>
              I have turned off my browser extensions, or I&rsquo;m using a
              private window.
            </ConsentCheckbox>
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            className="w-full cursor-pointer min-h-11"
            disabled={!confirmed}
            onClick={onContinue}
          >
            Continue
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
